import { useState } from 'react';
import { translate } from '@washed/i18n';
import type { FormState } from '../routes/HomePage.js';
import { Reveal } from '../motion/Reveal.js';
import { MaskedReveal } from '../motion/MaskedReveal.js';
import { brandify } from '../components/Brand.js';
import { MascotMotion } from '../components/MascotMotion.js';
import { QuartierCombobox } from '../components/QuartierCombobox.js';
import { TurnstileWidget } from '../components/TurnstileWidget.js';
import { FormField } from '../components/FormField.js';
import { useWaitlistForm } from '../components/useWaitlistForm.js';
import { ConsentCheckbox } from '../components/ConsentCheckbox.js';
import { LOME_QUARTIERS_SORTED } from '../data/lome-quartiers.js';
import styles from './Closing.module.css';

export interface ClosingSubmission {
  phone: string;
  quartier?: string;
  email?: string;
  source: 'closing';
  consentTerms: boolean;
  cfTurnstileResponse?: string;
}

export interface ClosingProps {
  locale: 'fr' | 'en';
  onSubmit: (input: ClosingSubmission) => void;
  state?: FormState;
}

export function Closing({ locale, onSubmit, state = { kind: 'idle' } }: ClosingProps) {
  const [phone, setPhone] = useState('');
  const [quartier, setQuartier] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const form = useWaitlistForm(state, locale);
  const t = (k: Parameters<typeof translate>[0]) => translate(k, locale);
  const busy = state.kind === 'loading' || form.awaitingChallenge;
  const handle = (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    onSubmit({
      phone,
      source: 'closing',
      consentTerms: consent,
      ...(quartier.trim() ? { quartier: quartier.trim() } : {}),
      ...(email.trim() ? { email: email.trim() } : {}),
      ...(form.token ? { cfTurnstileResponse: form.token } : {}),
    });
  };

  const formContent =
    state.kind === 'success' ? (
      <p ref={form.successRef} tabIndex={-1} role="status" className={styles.successMsg}>
        {translate('marketing.closing.form.success', locale, { position: state.position })}
      </p>
    ) : (
      <form className={styles.form} onSubmit={handle}>
        <FormField
          label={t('marketing.form.label.phone')}
          hint={t('marketing.form.hint.phone')}
          error={form.phoneError}
        >
          {(control) => (
            <input
              {...control}
              ref={form.phoneRef}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              required
              placeholder={t('marketing.closing.form.placeholder.phone')}
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                form.clearErrorFor('phone');
              }}
              className={styles.input}
            />
          )}
        </FormField>
        <FormField label={t('marketing.form.label.quartier')}>
          {(control) => (
            <QuartierCombobox
              inputId={control.id}
              ariaDescribedBy={control['aria-describedby']}
              value={quartier}
              onChange={setQuartier}
              options={LOME_QUARTIERS_SORTED}
              placeholder={t('marketing.closing.form.placeholder.quartier')}
              listLabel={t('marketing.form.label.quartier')}
              inputClassName={styles.input}
            />
          )}
        </FormField>
        <FormField label={t('marketing.form.label.email')} error={form.emailError}>
          {(control) => (
            <input
              {...control}
              ref={form.emailRef}
              type="email"
              inputMode="email"
              autoComplete="email"
              className={styles.input}
              placeholder={t('marketing.closing.form.placeholder.email')}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                form.clearErrorFor('email');
              }}
            />
          )}
        </FormField>
        <TurnstileWidget {...form.turnstile} />
        <ConsentCheckbox locale={locale} checked={consent} onChange={setConsent} />
        <button type="submit" className={styles.submit} disabled={busy}>
          {state.kind === 'loading'
            ? t('marketing.closing.form.loading')
            : form.awaitingChallenge
              ? t('marketing.form.verifying')
              : t('marketing.closing.form.submit')}
        </button>
        {form.formError && (
          <p role="alert" className={styles.errorMsg}>
            {form.formError}
          </p>
        )}
      </form>
    );

  return (
    <section id="rejoindre" className={styles.closing}>
      <Reveal className={styles.mascot} variant="scale" aria-hidden={true}>
        <MascotMotion
          kind="thread"
          mode="cta"
          width={100}
          height={120}
          className={styles.mascotMotion}
        />
      </Reveal>
      <Reveal as="span" className={`smallcaps ${styles.eyebrow}`}>
        {t('marketing.closing.eyebrow')}
      </Reveal>
      <Reveal as="h2" variant="mask" className={styles.h2}>
        <MaskedReveal>{brandify(t('marketing.closing.h2'))}</MaskedReveal>
      </Reveal>
      <Reveal as="p" className={styles.invitation} i={2}>
        <span className={styles.lead}>{t('marketing.closing.lead')}</span>
        <br />
        {t('marketing.closing.invitation')}
      </Reveal>
      {formContent}
      <p className={styles.fine}>{t('marketing.closing.fine')}</p>
    </section>
  );
}
